/**
 * Language-to-locale mapping for TTS. Uses centralized tts/languageMapping for native-quality locales.
 * Explicit mappings prevent ambiguous cyrillic text (e.g. Ukrainian) from being spoken in the wrong language (e.g. Russian).
 */
import { resolveEffectiveSpeechLocale } from "./cardLanguageUtils";
import { resolveBestLocale } from "./tts/languageMapping";

const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
const ZERO_WIDTH_AND_VARIATION_REGEX = /[\u200D\uFE0E\uFE0F]/g;

/**
 * Strips short optional suffixes in parentheses attached to words for TTS.
 * e.g. étudiant(e) -> étudiant, ami(e) -> ami, content(e) -> content.
 * Does NOT touch parenthetical annotations with space before paren, e.g. "parler (familier)".
 * Limit 25 chars inside parens to avoid removing meaningful parentheticals.
 */
const OPTIONAL_BRACKET_SUFFIX_REGEX = /([^\s(]+)(\([^)]{1,25}\))/g;

function stripOptionalBracketSuffixes(text: string): string {
  return text.replace(OPTIONAL_BRACKET_SUFFIX_REGEX, "$1");
}

export function sanitizeTextForSpeech(text: string): string {
  let s = (text ?? "")
    .normalize("NFKC")
    .replace(ZERO_WIDTH_AND_VARIATION_REGEX, "")
    .replace(EMOJI_REGEX, " ")
    .replace(/\s+/g, " ")
    .trim();
  s = stripOptionalBracketSuffixes(s);
  return s.replace(/\s+/g, " ").trim();
}

/** Maps language code to BCP-47 locale. Uses centralized tts/languageMapping for regional variants. */
export function toSpeechLocale(languageCode?: string, fallback = "en-US"): string {
  const raw = (languageCode ?? "").toString().trim();
  if (!raw) return fallback;
  return resolveBestLocale(raw, fallback);
}

/**
 * Resolves TTS language with strict priority. Used when explicit card language must never be overridden by text detection.
 * Priority: 1) explicitFieldLanguage, 2) sharedDeckLanguage, 3) deckDefaultLanguage, 4) detectedFallback (only when no explicit config).
 */
export function resolveSpeechLanguage(options: {
  explicitFieldLanguage?: string | null;
  sharedDeckLanguage?: string | null;
  deckDefaultLanguage?: string;
  detectedFallback?: string | null;
}): string {
  const { explicitFieldLanguage, sharedDeckLanguage, deckDefaultLanguage, detectedFallback } = options;
  const explicit = (explicitFieldLanguage ?? "").toString().trim();
  if (explicit) return toSpeechLocale(explicit, "en-US");
  const shared = (sharedDeckLanguage ?? "").toString().trim();
  if (shared) return toSpeechLocale(shared, "en-US");
  const deck = (deckDefaultLanguage ?? "").toString().trim();
  if (deck) return toSpeechLocale(deck, "en-US");
  const detected = (detectedFallback ?? "").toString().trim();
  return toSpeechLocale(detected, "en-US");
}

/**
 * Resolves TTS locale for a card side. Uses resolveEffectiveSpeechLocale for full priority chain:
 * 1) explicit frontLang/backLang, 2) uk/ru conflict override, 3) deckLang, 4) text detection, 5) fallback.
 */
export function resolveCardSideSpeechLocale(
  card: { front?: string; back?: string; frontLang?: string | null; backLang?: string | null },
  side: "front" | "back",
  deckLang?: string,
  fallback = "en-US"
): string {
  return resolveEffectiveSpeechLocale({ card, side, deckLang, fallback });
}
